import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../components/Admissionform/ToastManager';
import '../Login/StudentProfile.css';
import '../styles/Staffboard.css';
import ProfileUploadModal from '../Login/ProfileUploadModal';
import ImageCropper from '../components/Studentpage/ImageCropper';
import CameraModal from '../components/Studentpage/CameraModal';
import PersonalInfoSection from '../components/Studentpage/PersonalInfoSection';
import EducationSection from '../components/Studentpage/EducationSection';
import CourseInfoSection from '../components/Studentpage/CourseInfoSection';
import CourseProgressSection from '../components/Studentpage/CourseProgressSection';
import Technologies from '../Login/Technologies';
import ProfileSidebar from '../components/Studentpage/ProfileSidebar';
import PlacementSection from '../components/Studentpage/PlacementSection'
import { useSoftMandatoryWarning } from "../components/Studentpage/SoftMandatoryWarning";
import Swal from "sweetalert2";

const Studentdb = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [studentData, setStudentData] = useState(null);
    const [editableData, setEditableData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showUploadOptions, setShowUploadOptions] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);
    const [activeTab, setActiveTab] = useState('placement');
    const { showSuccess, showError } = useToast();
    const videoRef = useRef(null);
    const { getSoftMandatoryWarnings } = useSoftMandatoryWarning(editableData, studentData);
    const softPopupShownRef = useRef(false);
    const [profileLoaded, setProfileLoaded] = useState(false);


    useEffect(() => {
        console.log('Component mounted, checking for student data...');
        let studentInfo = null;

        if (location.state && location.state.student) {
            console.log('Found student data in location.state:', location.state.student);
            // location.state survives refresh and may be a stale login snapshot;
            // localStorage for the same student is always at least as recent.
            let source = location.state.student;
            try {
                const stored = JSON.parse(localStorage.getItem('studentData') || 'null');
                if (stored?.studentId && stored.studentId === source.studentId) {
                    source = stored;
                }
            } catch (e) {
                console.warn('Ignoring invalid studentData in localStorage:', e);
            }
            studentInfo = normalizeDesiredLocation(source);
            setStudentData(studentInfo);
            setEditableData(studentInfo);
            localStorage.setItem('studentData', JSON.stringify(studentInfo));
        } else {
            try {
                const storedData = localStorage.getItem('studentData');
                console.log('Raw localStorage data:', storedData);

                if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    console.log('Parsed localStorage data:', parsedData);

                    if (parsedData && parsedData.studentId) {
                        studentInfo = normalizeDesiredLocation(parsedData);
                        setStudentData(studentInfo);
                        setEditableData(studentInfo);
                        localStorage.setItem('studentData', JSON.stringify(studentInfo));
                    } else {
                        console.warn('Invalid student data structure in localStorage');
                        navigate('/');
                        return;
                    }
                } else {
                    console.log('No student data in localStorage, redirecting to login');
                    navigate('/');
                    return;
                }
            } catch (error) {
                console.error('Error parsing localStorage data:', error);
                localStorage.removeItem('studentData');
                navigate('/');
                return;
            }
        }

        if (studentInfo?.studentId) {
            fetchLatestProfileData(studentInfo.studentId);
        }
    }, [location, navigate]);
    // ⭐ Show soft mandatory popup when profile loads
    useEffect(() => {
        if (!profileLoaded) return;
        if (!editableData || !studentData) return;

        // Prevent duplicate popup on rerenders (Strict Mode)
        if (softPopupShownRef.current) return;

        const warnings = getSoftMandatoryWarnings();
        if (warnings.length > 0) {
            setTimeout(() => {
                showSoftMandatoryPopup(warnings);
            }, 500);
        }

        softPopupShownRef.current = true;
    }, [editableData, studentData]);


    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const normalizeDesiredLocation = (student) => {
        const desiredLoc = student.courseDetails?.desiredlocation;
        const skillSet = student.courseDetails?.skillSet;
        const knownSkill = student.courseDetails?.knownSkill;

        const collegeDepartment = student.department;
        const techDepartment = student.Department || student.selectedDepartment || student.courseDetails?.Department;

        const fixedDesiredLocation = typeof desiredLoc === 'string'
            ? desiredLoc.split(',').map(s => s.trim())
            : Array.isArray(desiredLoc)
                ? desiredLoc
                : [];

        const fixedSkillSet = typeof skillSet === 'string'
            ? skillSet.split(',').map(s => s.trim())
            : Array.isArray(skillSet)
                ? skillSet
                : [];

        const fixedKnownSkills = typeof knownSkill === 'string'
            ? knownSkill.split(',').map(s => s.trim())
            : Array.isArray(knownSkill)
                ? knownSkill
                : [];

        return {
            ...student,
            desiredlocation: fixedDesiredLocation,
            selectedSkillSet: fixedSkillSet,
            selectedKnownSkills: fixedKnownSkills,
            department: collegeDepartment,
            selectedDepartment: techDepartment,
        };
    };

    const fetchLatestProfileData = async (studentId) => {
        try {
            console.log('Fetching latest profile data for student:', studentId);
            const token = localStorage.getItem("studentToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/student/${studentId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Fetch Profile Response:", response.data);

            if (response.data.success && response.data.student) {
                const latestData = normalizeDesiredLocation(response.data.student);
                console.log('Latest data from server:', latestData);
                console.log(
                    'PROFILE IMAGE PATH FROM LATEST API:',
                    latestData.profilePicPath
                );

                setStudentData(latestData);
                setEditableData(latestData);
                localStorage.setItem('studentData', JSON.stringify(latestData));
                console.log('State updated with latest data');
                setProfileLoaded(true);
            }
        } catch (error) {
            console.error('Error fetching latest profile data:', error);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);

        // ⭐ Keep your original functionality
        const editData = normalizeDesiredLocation(studentData);
        console.log('Starting edit mode with data:', editData);

        // ⭐ Step 1: Set your normalized data first
        setEditableData(editData);

        // ⭐ Step 2: After state update, merge Experience fields
        setEditableData(prev => {
            const cd = studentData.courseDetails || {};

            const experienceDetails = [
                {
                    companyName: cd.expCompanyName1 || "",
                    startDate: cd.expStartDate1 || "",
                    endDate: cd.expEndDate1 || "",
                    role: cd.expRole1 || "",
                    technologies: cd.expTechnologies1 || "",
                    currentlyWorking: cd.expCurrentlyWorking1 || false
                },
                {
                    companyName: cd.expCompanyName2 || "",
                    startDate: cd.expStartDate2 || "",
                    endDate: cd.expEndDate2 || "",
                    role: cd.expRole2 || "",
                    technologies: cd.expTechnologies2 || "",
                    currentlyWorking: cd.expCurrentlyWorking2 || false
                },
                {
                    companyName: cd.expCompanyName3 || "",
                    startDate: cd.expStartDate3 || "",
                    endDate: cd.expEndDate3 || "",
                    role: cd.expRole3 || "",
                    technologies: cd.expTechnologies3 || "",
                    currentlyWorking: cd.expCurrentlyWorking3 || false
                }
            ].filter(r =>
                r.companyName || r.role || r.technologies || r.startDate || r.endDate
            );

            return {
                ...prev,      // ← preserves desiredlocation, selected skills, etc.
                gender: studentData.gender || studentData.courseDetails?.gender || '',
                experience: cd.experience || "",
                experienceDetails: experienceDetails.length > 0 ? experienceDetails : []
            };
        });
    };
    const BASIC_FIELDS = [
        "Student Name",
        "UG Degree",
        "UG Department",
        "UG Passout",
        "UG Percentage",
        "PG Degree",
        "PG Passout",
        "PG Department",
        "PG Percentage",
        "Experience",
        "Preferred Job Location",
        "Student Contact Number",
        "Gender",
        "10th Percentage",
        "12th Percentage",
        "Resume"
    ];

    const SCORE_FIELDS = [
        "Technical Score",
        "Communication Score",
        "Mock Interview",
        "Project 1 Score",
        "Mock Test 1 Score",
    ];

    const showSoftMandatoryPopup = (warnings) => {
        const basicWarnings = warnings.filter(w => BASIC_FIELDS.includes(w));
        const scoreWarnings = warnings.filter(w => SCORE_FIELDS.includes(w));

        // ⭐ NEW → Detect messages such as "Please fill qualification"
        const generalWarnings = warnings.filter(
            w => !BASIC_FIELDS.includes(w) && !SCORE_FIELDS.includes(w)
        );

        Swal.fire({
            icon: "warning",
            title: "Important Data Missing!!",
            html: `

      ${generalWarnings.length > 0 ? `
        <p style="
          margin-top:10px;
          margin-bottom:20px;
          font-size:18px;
          font-weight:800;
          text-align:center;
          color:#d10000;
        ">
          ${generalWarnings[0]}
        </p>
      ` : ""}

      ${basicWarnings.length > 0 ? `
        <p style="
          margin-top:10px;
          margin-bottom:10px;
          font-size:16px;
          font-weight:700;
          color:#b30000;">
          Please fill in the details below to help us match your profile with the right job opportunities.
        </p>

        <ul style="text-align:left; padding-left:20px; margin-bottom:20px;">
          ${basicWarnings.map(w => `<li>${w}</li>`).join("")}
        </ul>
      ` : ""}

      ${scoreWarnings.length > 0 ? `
        <p style="
          margin-top:15px;
          margin-bottom:10px;
          font-size:16px;
          font-weight:700;
          color:#b30000;
        ">
          Your evaluation and assessment score is still pending, and you may not be eligible for all placement opportunities.
          Please work with your trainer or mentor and get these details updated immediately.
        </p>

        <ul style="text-align:left; padding-left:20px;">
          ${scoreWarnings.map(w => `<li>${w}</li>`).join("")}
        </ul>
      ` : ""}
    `,
            confirmButtonText: "Noted"
        });
    };



    const handleSave = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                name: editableData.name,
                email_Id: editableData.email_Id,
                contactNo: editableData.contactNo,
                ParentNo: editableData.ParentNo,
                address: editableData.address,
                educationLevel: editableData.educationLevel,
                department: editableData.department,
                Department: editableData.selectedDepartment,
                clg_name: editableData.clg_name,
                educationCourse: editableData.educationCourse,
                dob: editableData.dob,
                studentRequirement: editableData.studentRequirement,
                desiredlocation: Array.isArray(editableData.desiredlocation)
                    ? editableData.desiredlocation
                    : [editableData.desiredlocation].filter(Boolean),
                gender: editableData.gender,
                tenthPassout: editableData.tenthPassout,
                tenthPercentage: editableData.tenthPercentage,
                tenthInstitution: editableData.tenthInstitution,
                tenthDepartment: editableData.tenthDepartment || '',
                tenthDegree: editableData.tenthDegree || '',
                twelfthPassout: editableData.twelfthPassout,
                twelfthPercentage: editableData.twelfthPercentage,
                twelfthInstitution: editableData.twelfthInstitution,
                twelfthDepartment: editableData.twelfthDepartment || '',
                twelfthDegree: editableData.twelfthDegree || '',
                diplomaInstitution: editableData.diplomaInstitution,
                diplomaDepartment: editableData.diplomaDepartment,
                diplomaDegree: editableData.diplomaDegree,
                diplomaPassout: editableData.diplomaPassout,
                diplomaPercentage: editableData.diplomaPercentage,
                ugInstitution: editableData.ugInstitution,
                ugDepartment: editableData.ugDepartment,
                ugDegree: editableData.ugDegree,
                ugPassout: editableData.ugPassout,
                ugPercentage: editableData.ugPercentage || editableData.cgpa,
                pgInstitution: editableData.pgInstitution,
                pgDepartment: editableData.pgDepartment,
                pgDegree: editableData.pgDegree,
                pgPassout: editableData.pgPassout,
                pgPercentage: editableData.pgPercentage,
                skillSet: Array.isArray(editableData.selectedSkillSet)
                    ? editableData.selectedSkillSet
                    : [],
                knownSkill: Array.isArray(editableData.selectedKnownSkills)
                    ? editableData.selectedKnownSkills
                    : [],

                experience: editableData.experience,

                // ===== EXPERIENCE ROW 1 =====
                expCompanyName1: editableData.experienceDetails?.[0]?.companyName || null,
                expStartDate1: editableData.experienceDetails?.[0]?.startDate || null,
                expEndDate1: editableData.experienceDetails?.[0]?.endDate || null,
                expRole1: editableData.experienceDetails?.[0]?.role || null,
                expTechnologies1: editableData.experienceDetails?.[0]?.technologies || null,
                expCurrentlyWorking1: editableData.experienceDetails?.[0]?.currentlyWorking || false,

                // ===== EXPERIENCE ROW 2 =====
                expCompanyName2: editableData.experienceDetails?.[1]?.companyName || null,
                expStartDate2: editableData.experienceDetails?.[1]?.startDate || null,
                expEndDate2: editableData.experienceDetails?.[1]?.endDate || null,
                expRole2: editableData.experienceDetails?.[1]?.role || null,
                expTechnologies2: editableData.experienceDetails?.[1]?.technologies || null,
                expCurrentlyWorking2: editableData.experienceDetails?.[1]?.currentlyWorking || false,

                // ===== EXPERIENCE ROW 3 =====
                expCompanyName3: editableData.experienceDetails?.[2]?.companyName || null,
                expStartDate3: editableData.experienceDetails?.[2]?.startDate || null,
                expEndDate3: editableData.experienceDetails?.[2]?.endDate || null,
                expRole3: editableData.experienceDetails?.[2]?.role || null,
                expTechnologies3: editableData.experienceDetails?.[2]?.technologies || null,
                expCurrentlyWorking3: editableData.experienceDetails?.[2]?.currentlyWorking || false,
            };

            console.log('Payload being sent to server:', payload);
            const token = localStorage.getItem("studentToken");

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/studentpro/${editableData.studentId}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log('Server response:', response.data);

            if (response.data.success) {
                const updatedData = {
                    ...studentData,
                    ...payload,
                    studentId: editableData.studentId,
                    desiredlocation: payload.desiredlocation,
                    selectedSkillSet: payload.skillSet,
                    selectedKnownSkills: payload.knownSkill,
                    selectedDepartment: payload.Department
                };

                const normalizedUpdatedData = normalizeDesiredLocation(updatedData);
                setStudentData(normalizedUpdatedData);
                setEditableData(normalizedUpdatedData);
                localStorage.setItem('studentData', JSON.stringify(normalizedUpdatedData));
                setIsEditing(false);
                showSuccess('Profile updated successfully!');
                setTimeout(() => {
                    const softWarnings = getSoftMandatoryWarnings();
                    if (softWarnings.length > 0) {
                        showSoftMandatoryPopup(softWarnings);
                    }
                }, 2000);
                fetchLatestProfileData(editableData.studentId);
            } else {
                throw new Error(response.data.message || 'Failed to update profile');
            }
        } catch (err) {
            console.error('Save error:', err);
            const errorMsg = err.response?.data?.message || err.message || 'Failed to update profile';
            setError(errorMsg);
            showError(`${errorMsg}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        const softWarnings = getSoftMandatoryWarnings();
        if (softWarnings.length > 0) {
            showSoftMandatoryPopup(softWarnings);
        }
        setIsEditing(false);
        setEditableData(studentData);
        setError(null);
    };

    const handleInputChange = (field, value) => {
        setEditableData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            showError('Only PDF files are allowed.');
            event.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('resume', file);
        formData.append('studentId', studentData.studentId);

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/upload-resume`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                showSuccess('Resume uploaded successfully!');
                console.log('Resume upload success:', res.data);
            } else {
                throw new Error(res.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Resume upload error:', error);
            showError(error.response?.data?.message || 'Failed to upload resume.');
        } finally {
            event.target.value = '';
        }
    };

    const handleProfileUpload = async (event) => {
        const file = event.target.files[0];
        console.log('Selected file:', file.name, file.type);
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            setShowImageCropper(true);
            setSelectedImageForCrop(e.target.result);
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const handleCroppedImageUpload = async (croppedBlob, originalFileName = 'cropped-profile.png') => {
        // Extract extension from original filename
        const extension = originalFileName.split('.').pop() || 'png';
        const fileName = `cropped-profile.${extension}`;

        const formData = new FormData();
        formData.append('profilePic', croppedBlob, fileName);
        formData.append('studentId', studentData.studentId);

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/upload-profile`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                const profilePicPath = res.data.filePath + '?t=' + Date.now();
                const updatedData = {
                    ...studentData,
                    profilePicPath: profilePicPath,
                };

                setStudentData(updatedData);
                setEditableData(updatedData);
                localStorage.setItem('studentData', JSON.stringify(updatedData));
                showSuccess('Profile picture uploaded successfully!');
            } else {
                throw new Error(res.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Profile upload error:', error);
            showError(error.response?.data?.message || 'Failed to upload profile picture.');
        } finally {
            setShowImageCropper(false);
            setSelectedImageForCrop(null);
        }
    };


    const handleCancelCrop = () => {
        setShowImageCropper(false);
        setSelectedImageForCrop(null);
    };

    const handleLogout = () => {
        console.log('Logging out...');
        localStorage.removeItem('studentData');
        localStorage.removeItem('studentToken');
        setStudentData(null);
        setEditableData(null);
        navigate('/');
    };

    const handleProfileUploadClick = () => {
        setShowUploadOptions(true);
    };

    const closeCameraModal = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setShowCamera(false);
        setCapturedImage(null);
    };


    const getProfileImageUrl = (profilePicPath) => {
        if (!profilePicPath) return null;

        // Handle an already-complete image URL
        if (/^https?:\/\//i.test(profilePicPath)) {
            return profilePicPath;
        }

        const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
        const path = profilePicPath.startsWith('/')
            ? profilePicPath
            : `/${profilePicPath}`;

        return `${baseUrl}${path}`;
    };


    if (!studentData) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px'
            }}>
                Loading student profile...
            </div>
        );
    }

    return (
        <div className="profile-container">


            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className="profile-content">
                {/* Sidebar Component */}
                <ProfileSidebar
                    studentData={studentData}
                    isEditing={isEditing}
                    isLoading={isLoading}
                    onEdit={handleEdit}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onLogout={handleLogout}
                    onProfileUploadClick={handleProfileUploadClick}
                    onResumeUpload={handleResumeUpload}
                    getProfileImageUrl={getProfileImageUrl}
                    navigate={navigate}
                />

                {/* Modals */}
                {showImageCropper && selectedImageForCrop && (
                    <div className="modal-overlay">
                        <ImageCropper
                            imageSrc={selectedImageForCrop}
                            onCrop={handleCroppedImageUpload}
                            onCancel={handleCancelCrop}
                        />
                    </div>
                )}

                {showUploadOptions && (
                    <ProfileUploadModal
                        isOpen={showUploadOptions}
                        onClose={() => setShowUploadOptions(false)}
                        onCameraCapture={() => setShowCamera(true)}
                        onFileUpload={handleProfileUpload}
                        studentData={studentData}
                    />
                )}

                {showCamera && (
                    <CameraModal
                        showCamera={showCamera}
                        capturedImage={capturedImage}
                        videoRef={videoRef}
                        cameraStream={cameraStream}
                        setCameraStream={setCameraStream}
                        setCapturedImage={setCapturedImage}
                        onClose={closeCameraModal}
                        onUsePhoto={async (blob) => {
                            const formData = new FormData();
                            formData.append('profilePic', blob, 'captured-photo.jpg');
                            formData.append('studentId', studentData.studentId);

                            try {
                                const res = await axios.post(`${process.env.REACT_APP_API_URL}/upload-profile`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                });

                                if (res.data.success) {
                                    const profilePicPath = res.data.filePath + '?t=' + Date.now();
                                    const updatedData = {
                                        ...studentData,
                                        profilePicPath: profilePicPath,
                                    };

                                    setStudentData(updatedData);
                                    setEditableData(updatedData);
                                    localStorage.setItem('studentData', JSON.stringify(updatedData));
                                    showSuccess('Photo captured and uploaded successfully!');
                                } else {
                                    throw new Error(res.data.message || 'Upload failed');
                                }
                            } catch (error) {
                                console.error('Profile upload error:', error);
                                showError(error.response?.data?.message || 'Failed to upload profile picture.');
                            } finally {
                                closeCameraModal();
                            }
                        }}
                    />
                )}

                {/* Main Content */}
                <div className="profile-main">
                    <div className="tabs-container">
                        <div className="tabs">
                            <button
                                className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                                onClick={() => handleTabChange('personal')}
                            >
                                Personal Info
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'tech' ? 'active' : ''}`}
                                onClick={() => handleTabChange('tech')}
                            >
                                Skills
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'education' ? 'active' : ''}`}
                                onClick={() => handleTabChange('education')}
                            >
                                Qualification
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'course' ? 'active' : ''}`}
                                onClick={() => handleTabChange('course')}
                            >
                                Course Details
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'progress' ? 'active' : ''}`}
                                onClick={() => handleTabChange('progress')}
                            >
                                Course Progress
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'placement' ? 'active' : ''}`}
                                onClick={() => handleTabChange('placement')}
                            >
                                Placement
                            </button>
                        </div>
                    </div>

                    <div className="tab-content">
                        {activeTab === 'personal' && (
                            <PersonalInfoSection
                                studentData={studentData}
                                editableData={editableData}
                                isEditing={isEditing}
                                onInputChange={(f, v) =>
                                    setEditableData((p) => ({ ...p, [f]: v }))
                                }
                            />
                        )}

                        {activeTab === 'tech' && (
                            <Technologies
                                studentData={studentData}
                                editableData={editableData}
                                isEditing={isEditing}
                                onInputChange={(f, v) =>
                                    setEditableData((p) => ({ ...p, [f]: v }))
                                }
                                showError={showError}
                            />
                        )}

                        {activeTab === 'education' && (
                            <EducationSection
                                studentData={studentData}
                                editableData={editableData}
                                isEditing={isEditing}
                                onInputChange={(f, v) =>
                                    setEditableData((p) => ({ ...p, [f]: v }))
                                }
                            />
                        )}

                        {activeTab === 'course' && (
                            <CourseInfoSection
                                studentData={studentData}
                                editableData={editableData}
                                isEditing={isEditing}
                                onInputChange={(f, v) =>
                                    setEditableData((p) => ({ ...p, [f]: v }))
                                }
                            />
                        )}

                        {activeTab === 'progress' && (
                            <CourseProgressSection studentData={studentData} />
                        )}
                        {activeTab === "placement" && (
                            <PlacementSection
                                studentData={studentData}
                                warnings={profileLoaded ? getSoftMandatoryWarnings() : []}
                                onShowWarnings={showSoftMandatoryPopup}
                            />)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Studentdb;